import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { OnboardingJob, OnboardingJobInputKind, OnboardingJobItem } from "../../../../services/backend.service";
import { backendService } from "../../../../services/backend.service";
import { earliestDateInCsv, SHIFTS_DATE_COLUMN } from "../../loadOrder";
import {
	isOnboardingArtifactUnavailableError,
	isOnboardingJobActive,
	useCreateCsvTransformJob,
	useOnboardingJob,
	useRetryOnboardingJobItem,
} from "../../useOnboardingJobs";
import { type DataLoadFile, type DataLoadFileKind, fileTypeOf, newDataLoadFile } from "./consts";
import { errorMessage, nonEmptyTabInstructions } from "./dataLoadPayload";

type Params = {
	departmentId: string | undefined;
	files: DataLoadFile[];
	setFiles: Dispatch<SetStateAction<DataLoadFile[]>>;
	allowDuplicates: boolean;
	setAllowDuplicates: Dispatch<SetStateAction<boolean>>;
	minStartDate: string;
	setMinStartDate: Dispatch<SetStateAction<string>>;
	updateFile: (fileId: string, partial: Partial<DataLoadFile>) => void;
	onDone: () => void;
	restoredJob?: OnboardingJob;
	onJobCreated?: (job: OnboardingJob) => void;
};

const kindFromInput = (kind: OnboardingJobInputKind): DataLoadFileKind => (kind === "nurses" ? "roster" : kind);

const sourceFile = (item: OnboardingJobItem, bytes: ArrayBuffer): File => new File([bytes], item.display_filename);

/** Owns transformation submission as a durable onboarding job, and hydrates its per-file results. */
export function useDataLoadTransform({
	departmentId,
	files,
	setFiles,
	allowDuplicates,
	setAllowDuplicates,
	minStartDate,
	setMinStartDate,
	updateFile,
	onDone,
	restoredJob,
	onJobCreated,
}: Params) {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const [currentJobId, setCurrentJobId] = useState<string>();
	const [retryingFileId, setRetryingFileId] = useState<string>();
	const [artifactsUnavailable, setArtifactsUnavailable] = useState(false);
	const [sourceReadyItemIds, setSourceReadyItemIds] = useState<Set<string>>(() => new Set());
	const fileIdByItemId = useRef(new Map<string, string>());
	const hydratedItemStates = useRef(new Map<string, string>());
	const activeJobScope = useRef<string | undefined>(undefined);
	const hydrationGeneration = useRef(0);
	const acceptsHydrationCallbacks = useRef(true);
	const restoredJobId = useRef<string | undefined>(undefined);

	const createTransformJob = useCreateCsvTransformJob();
	const retryItemMutation = useRetryOnboardingJobItem();
	const jobQuery = useOnboardingJob(currentJobId, departmentId);
	const restoredJobForDepartment = restoredJob?.department_id === departmentId ? restoredJob : undefined;
	const currentJob = jobQuery.data ? jobQuery.data : restoredJobForDepartment?.id === currentJobId ? restoredJobForDepartment : undefined;
	const beginJobScope = useCallback((job: OnboardingJob): number => {
		const scope = `${job.department_id}:${job.id}`;
		if (activeJobScope.current === scope) return hydrationGeneration.current;
		activeJobScope.current = scope;
		hydrationGeneration.current += 1;
		fileIdByItemId.current.clear();
		hydratedItemStates.current.clear();
		setSourceReadyItemIds(new Set());
		return hydrationGeneration.current;
	}, []);
	const isCurrentJobScope = useCallback(
		(job: OnboardingJob, generation: number): boolean =>
			acceptsHydrationCallbacks.current && activeJobScope.current === `${job.department_id}:${job.id}` && hydrationGeneration.current === generation,
		[],
	);

	const runTransforms = async () => {
		if (!departmentId || files.length === 0 || files.some((entry) => entry.kind === null)) return;
		const configuredFiles = files.filter((entry): entry is DataLoadFile & { kind: DataLoadFileKind } => entry.kind !== null);
		if (configuredFiles.length !== files.length) return;
		const hasScheduleFile = configuredFiles.some((entry) => entry.kind === "shifts" || entry.kind === "template");
		const hasTemplateFile = configuredFiles.some((entry) => entry.kind === "template");
		track("data_load_run_start", {
			department_id: departmentId,
			file_count: configuredFiles.length,
			transform_count: configuredFiles.filter((entry) => entry.mode === "transform").length,
			direct_count: configuredFiles.filter((entry) => entry.mode === "direct").length,
		});

		try {
			const job = await createTransformJob.mutateAsync({
				departmentId,
				idempotencyKey: crypto.randomUUID(),
				origin: "data_load",
				files: configuredFiles.map((entry) => entry.file),
				items: configuredFiles.map((entry) => ({
					input_kind: fileTypeOf(entry.kind),
					csv_instructions: entry.mode === "transform" ? entry.prompt.trim() : "",
					tab_instructions: entry.mode === "transform" ? nonEmptyTabInstructions(entry.tabInstructions) : {},
					sheet_name: null,
					transform: entry.mode === "transform",
				})),
				configuration: {
					allow_duplicates: hasScheduleFile && allowDuplicates,
					min_start_date: hasTemplateFile && minStartDate ? minStartDate : null,
					roster_actions: configuredFiles.map((entry) => (entry.kind === "roster" ? entry.rosterAction : null)),
				},
			});

			beginJobScope(job);
			fileIdByItemId.current = new Map(
				job.items.flatMap((item) => {
					const source = configuredFiles[item.ordinal];
					return source ? [[item.id, source.id]] : [];
				}),
			);
			setSourceReadyItemIds(new Set(job.items.map((item) => item.id)));
			setCurrentJobId(job.id);
			onDone();
			onJobCreated?.(job);
		} catch (error) {
			trackError("data_load_run_error", { department_id: departmentId, error_message: errorMessage(error) });
		}
	};

	useEffect(() => {
		if (!currentJob) return;
		const generation = hydrationGeneration.current;
		if (!isCurrentJobScope(currentJob, generation)) return;
		for (const item of currentJob.items) {
			const fileId = fileIdByItemId.current.get(item.id);
			if (!fileId || !sourceReadyItemIds.has(item.id)) continue;
			const hydrationState = `${item.status}:${item.prompt_history_id}:${item.user_error_key}`;
			if (hydratedItemStates.current.get(item.id) === hydrationState) continue;
			if (item.status === "failed" || item.status === "cancelled") {
				hydratedItemStates.current.set(item.id, hydrationState);
				updateFile(fileId, {
					transformError: item.user_error_key ? t(item.user_error_key) : t(`onboarding_jobs.item.${item.status}_description`),
					promptHistoryId: item.prompt_history_id,
				});
				continue;
			}
			if (item.status !== "succeeded") continue;
			if (!item.transform) {
				hydratedItemStates.current.set(item.id, hydrationState);
				updateFile(fileId, { promptHistoryId: item.prompt_history_id, transformError: null });
				continue;
			}
			hydratedItemStates.current.set(item.id, hydrationState);
			void backendService
				.getOnboardingJobItemOutput(currentJob.id, item.id, currentJob.department_id)
				.then((response) => {
					if (!isCurrentJobScope(currentJob, generation)) return;
					const kind = kindFromInput(item.input_kind);
					updateFile(fileId, {
						transformedData: response.data,
						failedTabs: item.failed_tabs,
						transformError: null,
						promptHistoryId: item.prompt_history_id,
						earliestDate: kind === "shifts" ? earliestDateInCsv(response.data, SHIFTS_DATE_COLUMN) : null,
					});
				})
				.catch((error) => {
					if (!isCurrentJobScope(currentJob, generation)) return;
					if (isOnboardingArtifactUnavailableError(error)) setArtifactsUnavailable(true);
					hydratedItemStates.current.delete(item.id);
					trackError("data_load_result_download_error", {
						department_id: currentJob.department_id,
						job_id: currentJob.id,
						item_id: item.id,
						error_message: errorMessage(error),
					});
					showErrorSnackbar(t("onboarding_jobs.errors.download_result"), { department_id: currentJob.department_id, job_id: currentJob.id }, error);
				});
		}
	}, [currentJob, isCurrentJobScope, showErrorSnackbar, sourceReadyItemIds, t, trackError, updateFile]);

	useEffect(() => {
		if (restoredJobForDepartment?.job_type !== "csv_transform_batch" || restoredJobId.current === restoredJobForDepartment.id) return;
		if (!restoredJobForDepartment.configuration) throw new Error("A restored Data Load job is missing its configuration");
		restoredJobId.current = restoredJobForDepartment.id;
		const configuration = restoredJobForDepartment.configuration;
		setAllowDuplicates(configuration.allow_duplicates);
		setMinStartDate(configuration.min_start_date === null ? "" : configuration.min_start_date);
		const generation = beginJobScope(restoredJobForDepartment);
		fileIdByItemId.current = new Map(restoredJobForDepartment.items.map((item) => [item.id, item.id]));
		setFiles([]);
		setCurrentJobId(restoredJobForDepartment.id);
		onDone();
		for (const item of restoredJobForDepartment.items) {
			void backendService
				.getOnboardingJobItemSource(restoredJobForDepartment.id, item.id, restoredJobForDepartment.department_id)
				.then((source) => {
					if (!isCurrentJobScope(restoredJobForDepartment, generation)) return;
					const entry = newDataLoadFile(sourceFile(item, source.data));
					entry.id = item.id;
					entry.kind = kindFromInput(item.input_kind);
					entry.mode = item.transform ? "transform" : "direct";
					const rosterAction = configuration.roster_actions[item.ordinal];
					if (entry.kind === "roster") {
						if (rosterAction === null || rosterAction === undefined) throw new Error("A restored roster item is missing its action");
						entry.rosterAction = rosterAction;
					}
					entry.promptHistoryId = item.prompt_history_id;
					entry.failedTabs = item.failed_tabs;
					setFiles((previous) => {
						const entriesById = new Map(previous.map((candidate) => [candidate.id, candidate]));
						entriesById.set(entry.id, entry);
						return restoredJobForDepartment.items.flatMap((candidate) => {
							const hydrated = entriesById.get(candidate.id);
							return hydrated ? [hydrated] : [];
						});
					});
					setSourceReadyItemIds((readyItemIds) => new Set([...readyItemIds, item.id]));
				})
				.catch((error) => {
					if (!isCurrentJobScope(restoredJobForDepartment, generation)) return;
					if (isOnboardingArtifactUnavailableError(error)) setArtifactsUnavailable(true);
					trackError("data_load_restore_error", {
						department_id: restoredJobForDepartment.department_id,
						job_id: restoredJobForDepartment.id,
						item_id: item.id,
						error_message: errorMessage(error),
					});
					showErrorSnackbar(
						t("onboarding_jobs.errors.restore"),
						{ department_id: restoredJobForDepartment.department_id, job_id: restoredJobForDepartment.id, item_id: item.id },
						error,
					);
				});
		}
	}, [beginJobScope, isCurrentJobScope, onDone, restoredJobForDepartment, setAllowDuplicates, setFiles, setMinStartDate, showErrorSnackbar, t, trackError]);

	useEffect(() => {
		acceptsHydrationCallbacks.current = true;
		return () => {
			acceptsHydrationCallbacks.current = false;
		};
	}, []);

	const retryFile = async (fileId: string): Promise<void> => {
		if (!currentJob || currentJob.department_id !== departmentId) return;
		const item = currentJob.items.find((candidate) => fileIdByItemId.current.get(candidate.id) === fileId);
		if (!item) return;
		setRetryingFileId(fileId);
		hydratedItemStates.current.delete(item.id);
		updateFile(fileId, { transformError: null, transformedData: null, failedTabs: [] });
		try {
			await retryItemMutation.mutateAsync({ departmentId: currentJob.department_id, jobId: currentJob.id, itemId: item.id });
		} finally {
			setRetryingFileId(undefined);
		}
	};

	const resetTransformState = () => {
		setCurrentJobId(undefined);
		setRetryingFileId(undefined);
		setArtifactsUnavailable(false);
		hydrationGeneration.current += 1;
		activeJobScope.current = undefined;
		fileIdByItemId.current.clear();
		hydratedItemStates.current.clear();
		setSourceReadyItemIds(new Set());
		restoredJobId.current = undefined;
	};

	return {
		isRecording: createTransformJob.isPending,
		isTransforming: isOnboardingJobActive(currentJob),
		runTransforms,
		retryFile,
		retryingFileId,
		currentJob,
		artifactsUnavailable,
		resetTransformState,
	};
}
